import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  Divider,
  IconButton,
  SvgIcon,
} from '@mui/material';
import { BellOff, MessageSquare, X } from 'lucide-react';
import { useUIStore } from '../../store';
import { usersApi } from '../../api/users.api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const NotificationMenu = ({ anchorEl, open, onClose }) => {
  const navigate = useNavigate();
  const { notifications, markNotificationRead, markAllNotificationsRead, showError } = useUIStore();
  const unreadNotifications = notifications.filter((notification) => !notification.read);

  const handleMarkRead = async (notificationId) => {
    try {
      await usersApi.markNotificationRead(notificationId);
      markNotificationRead(notificationId);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      showError('Failed to mark notification as read');
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await handleMarkRead(notification.id);
    }
    onClose();
    // Navigate based on notification type
    if (notification.type === 'message') {
      navigate('/chat');
    }
  };

  const handleViewAll = () => {
    onClose();
    navigate('/notifications');
  };

  const handleMarkAllRead = () => {
    if (unreadNotifications.length === 0) {
      return;
    }

    Promise.all(unreadNotifications.map((notification) => usersApi.markNotificationRead(notification.id)))
      .then(() => {
        markAllNotificationsRead();
      })
      .catch((error) => {
        console.error('Failed to mark all notifications as read:', error);
        showError('Failed to mark all notifications as read');
      });
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{
        elevation: 3,
        sx: {
          width: 360,
          maxHeight: 400,
          mt: 1.5,
          overflow: 'visible',
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">Notifications</Typography>
        <Box>
          <Button size="small" onClick={handleMarkAllRead} sx={{ mr: 1 }}>
            Read All
          </Button>
          <Button size="small" onClick={handleViewAll}>
            View All
          </Button>
        </Box>
      </Box>

      {/* Notification List */}
      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
        {unreadNotifications.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <SvgIcon component={BellOff} inheritViewBox sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {unreadNotifications.slice(0, 5).map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: notification.read ? 'transparent' : 'action.hover',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                }}
                onClick={() => handleNotificationClick(notification)}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkRead(notification.id);
                    }}
                  >
                    <SvgIcon component={X} inheritViewBox fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <SvgIcon component={MessageSquare} inheritViewBox fontSize="small" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: notification.read ? 400 : 600 }}
                    >
                      {notification.title || 'Notification'}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 200,
                        }}
                      >
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(notification.createdAt).fromNow()}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Menu>
  );
};

export default NotificationMenu;
